/* One company's price, with the buying and selling marked on it.
 *
 * The position screen could say what the average cost was and what the mark is,
 * and from those two numbers nobody can tell a fill bought halfway up a run from
 * one bought at the top. This draws the closes and puts each trade where it
 * happened, which answers that at a glance and cannot be got from an average.
 *
 * A marker sits at **its own price**, not at that day's close. They are usually
 * close together and occasionally not, and the gap is the interesting part: a
 * fill well above the close is a fill that chased. Both are real numbers and
 * neither is moved to make the picture tidy.
 *
 * Shapes carry the meaning, not colour (§11): a buy points up, a sell points
 * down, and an opening — which is an average and not a fill — is a hollow
 * diamond that looks like neither. Hand-drawn SVG, no charting library (§2). */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  moneyExact, moneyExactSigned, plural, price as fmtPrice, shares as fmtShares, shortDate, stamp,
} from '../lib/format';
import { accountName } from '../lib/accounts';
import { ticksFor } from '../lib/ticks';
import { earliestMark, fills, markerTotals, type Marker } from '../state/priceMarkers';
import { useStore } from '../state/store';
import type { Currency } from '../types';

const W = 340;
const H = 120;
const PAD_X = 6;
const PAD_Y = 12;

type Window = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | '2Y';

/* How far back each window reaches, and where the bars come from.
 *
 * Five of the seven are cut out of the two years of daily closes already
 * fetched for this symbol, so choosing between them costs nothing. The two
 * short ones cannot be: a day of trading is a single daily close, and a chart
 * of one point is not a chart. Those ask for finer bars, and only when opened.
 *
 * The bar sizes are the ones the upstream actually serves for each span —
 * five minutes over a day, half an hour over a week. Asking for minutes over a
 * month returns nothing rather than a lot. */
const WINDOWS: Record<Window, { days: number; intraday?: { range: string; interval: string } }> = {
  '1D': { days: 1, intraday: { range: '1d', interval: '5m' } },
  '1W': { days: 7, intraday: { range: '5d', interval: '30m' } },
  '1M': { days: 31 },
  '3M': { days: 92 },
  '6M': { days: 183 },
  '1Y': { days: 365 },
  '2Y': { days: 730 },
};

const ORDER: Window[] = ['1D', '1W', '1M', '3M', '6M', '1Y', '2Y'];

interface Day { date: string; close: number }

/** A daily bar is named by its day and a finer one keeps the whole instant, so
    both shapes arrive here and both have to land on the same axis. */
function toMs(stamp: string): number {
  return new Date(stamp.length <= 10 ? `${stamp}T00:00:00Z` : stamp).getTime();
}

export function PriceChart({
  symbol, currency, markers, avgCost,
}: {
  symbol: string;
  currency: Currency;
  markers: Marker[];
  /** Share-weighted, native currency. Drawn as a level rather than a point:
      it is a price that is known and a date that is not. Zero means the book
      has no cost on record, and nothing is drawn. */
  avgCost: number;
}) {
  const { loadDailyCloses, loadIntraday, state } = useStore();
  const accounts = state.data.accounts;
  const [days, setDays] = useState<Day[] | null>(null);
  const [bars, setBars] = useState<Day[] | null>(null);
  const [failed, setFailed] = useState(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [cursor, setCursor] = useState<{ x: number; i: number } | null>(null);

  /* The narrowest window that still reaches the first mark, so a position held
     two years does not open on a chart that starts after it was bought. */
  const plotted = useMemo(() => fills(markers), [markers]);
  const first = earliestMark(plotted);
  /* Opened on the narrowest window that still reaches the first mark, so a
     position held two years does not open on a chart starting after it was
     bought. Never on one of the short ones: those are for watching today, and
     a position's own screen opens on its own history. */
  const needed: Window = useMemo(() => {
    if (!first) return '1Y';
    const back = (Date.now() - toMs(first)) / 86_400_000;
    return back > WINDOWS['1Y'].days ? '2Y'
      : back > WINDOWS['6M'].days ? '1Y'
      : back > WINDOWS['3M'].days ? '6M'
      : back > WINDOWS['1M'].days ? '3M'
      : '1M';
  }, [first]);
  const [window, setWindow] = useState<Window>(needed);
  useEffect(() => { setWindow(needed); }, [needed]);

  useEffect(() => {
    let live = true;
    setFailed(false);
    void loadDailyCloses(symbol)
      .then((d: Day[]) => { if (live) { setDays(d); if (d.length < 2) setFailed(true); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [symbol, loadDailyCloses]);

  /* The finer bars, fetched only for the window that needs them and only once
     it is chosen. Keyed by both, so switching back and forth does not refetch
     inside the cache's few minutes. */
  const fine = WINDOWS[window].intraday;
  useEffect(() => {
    if (!fine) { setBars(null); return; }
    let live = true;
    setBars(null);
    void loadIntraday(symbol, fine.range, fine.interval)
      .then((d: Day[]) => { if (live) setBars(d); })
      .catch(() => { if (live) setBars([]); });
    return () => { live = false; };
  }, [symbol, fine?.range, fine?.interval, loadIntraday]);

  const view = useMemo(() => {
    /* The short windows draw the bars they asked for; the rest are cut out of
       the daily closes already held. A window still loading its bars draws
       nothing rather than falling back to a daily line that would answer a
       different question at the same size. */
    const source = fine ? bars : days;
    if (!source || source.length < 2) return null;

    const cutoff = Date.now() - WINDOWS[window].days * 86_400_000;
    const shown = source.filter((d) => toMs(d.date) >= cutoff);
    if (shown.length < 2) return null;
    const startMs = toMs(shown[0]!.date);
    const inWindow = plotted.filter((m) => toMs(m.at) >= startMs);

    /* Marker prices are in the domain as well as closes. A fill outside the
       range the closes cover is exactly the one worth seeing, and clipping it
       to the edge would draw it as something it was not. */
    const values = [...shown.map((d) => d.close), ...inWindow.map((m) => m.price)];
    if (avgCost > 0) values.push(avgCost);
    let lo = Math.min(...values);
    let hi = Math.max(...values);
    if (!(hi > lo)) { hi = lo + 1; lo -= 1; }
    const pad = (hi - lo) * 0.08;
    lo -= pad; hi += pad;

    const t0 = startMs;
    const t1 = toMs(shown[shown.length - 1]!.date);
    const at = (ms: number) => {
      const frac = t1 > t0 ? (ms - t0) / (t1 - t0) : 0;
      return PAD_X + Math.min(1, Math.max(0, frac)) * (W - PAD_X * 2);
    };

    /* Where a mark belongs on this line.
     *
     * A daily bar is named by its day and therefore drawn at midnight, but the
     * close it reports happened at the end of the session. A fill at five in
     * the afternoon, placed by its own timestamp, landed three quarters of a
     * day to the right of the very point it belongs to — about ten pixels on a
     * month, which is the difference between a dot on its day and a dot beside
     * it. On a chart whose entire job is "where did I buy", that is the error
     * that matters.
     *
     * The line has one point per trading day and no resolution finer than that,
     * so a mark on a day the line knows is drawn at that day's point exactly.
     * Anything else — a fill on a holiday, or on a weekend at a broker that
     * booked it there — keeps the timestamp, which is the honest answer when
     * there is no point of its own to sit on.
     *
     * Only where the bars are intraday does the timestamp place everything:
     * there each bar is a real instant a few minutes apart, and the precision
     * is measured rather than implied. */
    const byDay = new Map<string, number>();
    if (!fine) for (const d of shown) byDay.set(d.date.slice(0, 10), toMs(d.date));

    const x = (stamp: string) => at(byDay.get(stamp.slice(0, 10)) ?? toMs(stamp));
    const y = (v: number) => PAD_Y + (1 - (v - lo) / (hi - lo)) * (H - PAD_Y * 2);

    /* The close on a mark's own day, where the line has one.
     *
     * A mark is drawn at the price that was paid, which is the whole point of
     * putting it here — but a dot at 14.40 against a line at 15.40 sits in
     * empty space with nothing joining it to anything, and empty space reads as
     * a rendering fault rather than as a gap worth looking at. The close is
     * what the other end of that gap is. */
    const closeByDay = new Map<string, number>();
    for (const d of shown) closeByDay.set(d.date.slice(0, 10), d.close);
    const closeOn = (stamp: string) => closeByDay.get(stamp.slice(0, 10)) ?? null;

    /* What actually gets a dot.
     *
     * Two fills in one session are two facts and one place on the chart. Drawn
     * as two dots seven pixels apart they overlap into a lump that reads as a
     * rendering fault rather than as two purchases — which is what it looked
     * like, and there is no honest way to move either of them apart, because
     * the y is the price that was paid.
     *
     * So a day and a side get one mark, at the average of the fills behind it
     * weighted by size. That is not an invented number: it is the same
     * arithmetic the book keeps as `avgCost`, over the same fills, and every
     * one of them is still listed underneath with its own size and price. The
     * mark carries how many it stands for so a day bought twice does not look
     * like a day bought once. */
    const groups = new Map<string, { m: Marker; shares: number; paid: number; fills: number }>();
    for (const m of inWindow) {
      const key = `${m.at.slice(0, 10)}|${m.kind}`;
      const size = Math.abs(m.shares) || 1;
      const g = groups.get(key);
      if (!g) groups.set(key, { m, shares: size, paid: size * m.price, fills: 1 });
      else { g.shares += size; g.paid += size * m.price; g.fills += 1; }
    }
    const drawn = [...groups.values()].map((g) => ({
      ...g.m,
      price: g.shares > 0 ? g.paid / g.shares : g.m.price,
      fills: g.fills,
    }));

    /* Two or three round prices to read the line against. Without them the
       shape of a move is all there is, and the same wiggle is thirty cents or
       thirty dollars with nothing on screen to say which. */
    return { shown, inWindow, drawn, x, y, closeOn, ticks: ticksFor(lo, hi), lo, hi };
  }, [days, bars, fine, window, plotted, avgCost]);

  const path = useMemo(() => {
    if (!view) return '';
    return view.shown
      .map((d, i) => `${i === 0 ? 'M' : 'L'}${view.x(d.date).toFixed(2)},${view.y(d.close).toFixed(2)}`)
      .join(' ');
  }, [view]);

  /* What is under the finger, and what is near it.
   *
   * Landing on a mark's exact day is not something anybody can do on a phone —
   * two years across three hundred pixels is a week per pixel. So the nearest
   * mark within reach wins, and the day's close is what shows when none is.
   * The reach is in chart units so it does not change with the screen. */
  const NEAR_MARK = 9;

  const at = cursor !== null && view
    ? view.shown[Math.min(view.shown.length - 1, Math.max(0, Math.round(cursor.i)))]
    : null;

  const nearest = useMemo(() => {
    if (cursor === null || !view || view.inWindow.length === 0) return null;
    let best: { m: Marker; d: number } | null = null;
    for (const m of view.inWindow) {
      const d = Math.abs(view.x(m.at) - cursor.x);
      if (!best || d < best.d) best = { m, d };
    }
    return best && best.d <= NEAR_MARK ? best.m : null;
  }, [cursor, view]);

  /* Every mark that sits where the nearest one sits, so two fills in one
     session are read together.
     *
     * Compared by the x they were drawn at rather than by their timestamps. On
     * a daily line two fills an hour apart are one point, and comparing the
     * stamps made them two: the code said "every mark on the day" and the test
     * it used was exact equality, which a trade recorded at 14:02 and another
     * at 16:41 never pass. On an intraday line the same test still separates
     * them, because there they really are drawn apart. */
  const markersAt = nearest && view
    ? view.inWindow.filter((m) => Math.abs(view.x(m.at) - view.x(nearest.at)) < 0.5)
    : [];

  const move = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg || !view) return;
    const box = svg.getBoundingClientRect();
    // A chart that has not been laid out yet has no width, and dividing by it
    // puts NaN into the cursor — which draws a line nobody can see at a
    // position nothing matches.
    if (!(box.width > 0)) return;
    const frac = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    setCursor({ x: PAD_X + frac * (W - PAD_X * 2), i: frac * (view.shown.length - 1) });
  };

  const totals = markerTotals(markers);

  if (failed) {
    return (
      <div className="card card-pad" style={{ marginTop: 12 }}>
        <div className="dim" style={{ fontSize: 12 }}>
          No price history came back for {symbol}, so there is nothing to draw. The trades below are
          unaffected.
        </div>
      </div>
    );
  }

  return (
    <div className="card card-pad" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Price, and where you traded</span>
        <span className="dim" style={{ fontSize: 11, marginLeft: 'auto' }}>
          {at ? `${stamp(at.date.length > 10 ? at.date : `${at.date}T12:00:00Z`, fine !== undefined)} · ${fmtPrice(at.close, currency)}`
            : plotted.length > 0 ? plural(plotted.length, 'trade') + ' marked'
            : 'no trades on record'}
        </span>
      </div>

      {!view ? (
        <div className="dim" style={{ fontSize: 12, padding: '18px 0' }}>
          {fine && bars !== null && bars.length < 2
            /* Daily bars answering a request for finer ones is a price service
               that has not been updated, not a quiet market — and the two need
               telling apart, or the fix gets looked for in the wrong place. A
               daily bar is named by its day and nothing else is. */
            ? bars.some((b) => b.date.length <= 10)
              ? `The price service is still returning daily bars, so ${window} cannot be drawn. It needs deploying. The longer ranges are unaffected.`
              : `No ${window} bars came back. The market may have been shut throughout.`
            : 'Reading prices…'}
        </div>
      ) : (
        <div className="chart-wrap">
          {/* The gradations, as text over the chart rather than inside it.
              *
              * The SVG is stretched to whatever width it is given, so a `text`
              * element in it comes out horizontally squashed — the labels are
              * HTML sitting on top, which also lets them use the same face and
              * colour as every other small figure in the app. */}
          <div className="chart-scale" aria-hidden="true">
            {view.ticks.map((v) => (
              <span key={v} style={{ top: `${(view.y(v) / H) * 100}%` }}>
                {fmtPrice(v, currency)}
              </span>
            ))}
          </div>

          <svg
            ref={svgRef} className="chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
            role="img" aria-label={`Closing prices for ${symbol} with ${markers.length} trades marked`}
            onPointerDown={(e) => move(e.clientX)}
            onPointerMove={(e) => {
              // Dragging on a touch screen, and plain hovering with a mouse.
              if (e.buttons > 0 || e.pointerType === 'mouse') move(e.clientX);
            }}
            onPointerLeave={() => setCursor(null)}
          >
            {/* Behind everything, so the line and the marks sit over them. */}
            {view.ticks.map((v) => (
              <line key={v} x1={0} y1={view.y(v)} x2={W} y2={view.y(v)}
                    stroke="var(--edge)" strokeWidth="1" />
            ))}

            <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.6"
                  strokeLinejoin="round" strokeLinecap="round" />

            {at ? (
              <line x1={view.x(at.date)} y1={0} x2={view.x(at.date)} y2={H}
                    stroke="var(--edge2)" strokeWidth="1" />
            ) : null}

            {/* The average cost: a level, because that is a price the book knows
                and a date it does not. */}
            {avgCost > 0 ? (
              <line x1={PAD_X} y1={view.y(avgCost)} x2={W - PAD_X} y2={view.y(avgCost)}
                    stroke="var(--dim2)" strokeWidth="1" strokeDasharray="3 3" />
            ) : null}

            {/* A dot each, green in and red out.
                *
                * Filled for a buy and a ring for a sell, so the two are told
                * apart by shape as well as by colour (§11) — the same rule the
                * rest of the app follows, and the one that makes this readable
                * to anyone who cannot separate the two hues.
                *
                * The one under the finger grows and gets a halo, because on a
                * chart this dense the answer to "which one am I reading" has to
                * be on the chart and not only in the line beneath it. */}
            {view.drawn.map((m, i) => {
              const mx = view.x(m.at);
              const my = view.y(m.price);
              const key = `${m.at}-${m.kind}-${m.account}-${i}`;
              const buy = m.kind === 'buy';
              const colour = buy ? 'var(--up)' : 'var(--down)';
              // Lit by where it is drawn, matching how they are grouped above.
              const lit = nearest !== null && Math.abs(mx - view.x(nearest.at)) < 0.5;
              /* A hairline from the fill to that day's close.
                 *
                 * Without it a fill away from the line is a dot in the middle of
                 * nothing, which is how it read: two of them floating under a
                 * line they had no visible relation to. The stem makes the
                 * distance the subject — this is what you paid, that is where
                 * the day ended — instead of leaving it as an unexplained gap.
                 * Faint and behind the dot, because it is the annotation and the
                 * fill is the fact. */
              const dayClose = view.closeOn(m.at);
              const closeY = dayClose === null ? null : view.y(dayClose);
              return (
                <g key={key}>
                  {closeY !== null && Math.abs(closeY - my) > 1.5 ? (
                    <line
                      x1={mx} y1={my} x2={mx} y2={closeY}
                      stroke={colour} strokeWidth="1" opacity={lit ? 0.55 : 0.3}
                    />
                  ) : null}
                  {lit ? (
                    <circle cx={mx} cy={my} r="7.5" fill={colour} opacity="0.22" />
                  ) : null}
                  {/* A second, wider ring where the dot stands for more than
                      one fill, so a day that was bought twice does not look
                      like a day that was bought once. */}
                  {m.fills > 1 ? (
                    <circle cx={mx} cy={my} r={lit ? 6.6 : 5.8} fill="none"
                            stroke={colour} strokeWidth="1" opacity="0.65" />
                  ) : null}
                  <circle
                    cx={mx} cy={my} r={lit ? 4.6 : 3.6}
                    fill={buy ? colour : 'var(--bg)'}
                    stroke={buy ? 'var(--bg)' : colour}
                    strokeWidth={buy ? 1.4 : 2}
                  />
                </g>
              );
            })}
          </svg>
        </div>
      )}

      <div className="range-row">
        {ORDER.map((w) => (
          <button key={w} aria-pressed={window === w} onClick={() => setWindow(w)}>{w}</button>
        ))}
      </div>

      {/* What the lit dot is. Kept under the chart rather than floating over it:
          a bubble on a chart this small covers the line it is describing, and
          the thing being compared is the mark against the prices around it. */}
      {markersAt.length > 0 ? (
        <div className="kv-list" style={{ marginTop: 8 }}>
          {markersAt.map((m) => (
            <div className="kv" key={`${m.at}-${m.kind}-${m.account}`}>
              <span className={m.kind === 'buy' ? 'up' : 'down'} style={{ fontWeight: 600 }}>
                {m.kind === 'buy' ? 'Bought' : 'Sold'} {fmtShares(m.shares)}
              </span>
              <span className="dim" style={{ fontSize: 11 }}>
                {shortDate(m.at)} · {accountName(accounts, m.account)}
              </span>
              <span className="num" style={{ marginLeft: 'auto', fontWeight: 600 }}>
                {fmtPrice(m.price, currency)}
              </span>
            </div>
          ))}
          <div className="hint" style={{ marginTop: 0 }}>
            {markersAt.map((m) => {
              const total = Math.abs(m.shares) * Math.abs(m.price);
              const gain = m.kind === 'sell' && m.realizedGain !== undefined
                ? `, realised ${moneyExactSigned(m.realizedGain, currency as 'USD')}`
                : '';
              return `${m.kind === 'buy' ? 'Cost' : 'Brought'} ${moneyExact(total, currency as 'USD')}${gain}`;
            }).join(' · ')}
          </div>
        </div>
      ) : null}

      <div className="chart-legend" style={{ marginTop: 8 }}>
        {totals.buys > 0 ? (
          <span>
            <i className="legend-dot" style={{ background: 'var(--up)' }} /> bought {fmtShares(totals.bought)}
          </span>
        ) : null}
        {totals.sells > 0 ? (
          <span>
            <i className="legend-dot" style={{ borderColor: 'var(--down)' }} data-hollow="true" /> sold {fmtShares(totals.sold)}
          </span>
        ) : null}
        {avgCost > 0 ? <span>-- your average {fmtPrice(avgCost, currency)}</span> : null}
      </div>
    </div>
  );
}
